import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { X, Save, Download, FolderOpen, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ClockPanel from './ClockPanel';
import CategoriesEditor from './CategoriesEditor';
import OverviewPanel from './OverviewPanel';
import ExportView from './ExportView';
import { makeDefaultState, HOURS_TOTAL, HOURS_PER_TAB, DAY_LABELS, DAYS_TOTAL, filledClocks } from './defaults';
import { exportBuilderToPdf } from './pdfExport';
import * as api from './api';

// One tab per day of the week. Overview tab is the last one.
const TAB_COUNT = DAYS_TOTAL; // 7
const TAB_LABELS = DAY_LABELS;

export default function RadioClockBuilder({ open, onClose, user }) {
  const [state, setState] = useState(() => makeDefaultState());
  const [templateName, setTemplateName] = useState('');
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('t0');
  const [showLoader, setShowLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const tabRefs = useRef({});
  const exportViewRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.listTemplates()
      .then((rows) => setSavedTemplates(rows || []))
      .catch(() => setSavedTemplates([]));
  }, [open]);

  const updateHour = useCallback((hourIndex, rows) => {
    setState((prev) => {
      const next = { ...prev, hours: prev.hours.slice() };
      next.hours[hourIndex] = { rows };
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Δώσε ένα όνομα στο template πρώτα.');
      return;
    }
    setLoading(true);
    try {
      let saved;
      if (currentTemplateId) {
        saved = await api.updateTemplate(currentTemplateId, templateName.trim(), state);
      } else {
        saved = await api.createTemplate(templateName.trim(), state);
        setCurrentTemplateId(saved.id);
      }
      toast.success('Αποθηκεύτηκε ✅');
      const rows = await api.listTemplates();
      setSavedTemplates(rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Αποτυχία αποθήκευσης');
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async (id) => {
    setLoading(true);
    try {
      const doc = await api.getTemplate(id);
      const defaults = makeDefaultState();
      const loadedData = doc.data || {};
      const hours = Array.isArray(loadedData.hours) ? loadedData.hours : [];
      // Pad or truncate to exactly HOURS_TOTAL. This lets us load older
      // templates that were saved when TOTAL was 30 — the extra slots
      // just come in empty.
      const paddedHours = Array.from({ length: HOURS_TOTAL }, (_, i) => {
        const h = hours[i];
        return h && Array.isArray(h.rows) ? { rows: h.rows } : { rows: [] };
      });
      setState({
        categories: (loadedData.categories && loadedData.categories.length ? loadedData.categories : defaults.categories),
        speech_types: (loadedData.speech_types && loadedData.speech_types.length ? loadedData.speech_types : defaults.speech_types),
        hours: paddedHours,
      });
      setTemplateName(doc.name || '');
      setCurrentTemplateId(doc.id);
      setShowLoader(false);
      toast.success(`Φορτώθηκε: ${doc.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Αποτυχία φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Διαγραφή του template "${name}";`)) return;
    try {
      await api.deleteTemplate(id);
      const rows = await api.listTemplates();
      setSavedTemplates(rows || []);
      if (currentTemplateId === id) {
        setCurrentTemplateId(null);
        setState(makeDefaultState());
        setTemplateName('');
      }
      toast.success('Διαγράφηκε');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Αποτυχία διαγραφής');
    }
  };

  const handleNew = () => {
    if (!window.confirm('Ξεκίνημα καινούριου template; Τα μη-αποθηκευμένα στοιχεία θα χαθούν.')) return;
    setState(makeDefaultState());
    setCurrentTemplateId(null);
    setTemplateName('');
    setActiveTab('t0');
  };

  // Jump from the Overview mosaic to a specific hour in edit mode:
  // switches to the correct day tab, waits for the panel to render,
  // then scrolls to it and flashes a highlight ring.
  const scrollToHour = useCallback((hourIndex) => {
    const day = Math.floor(hourIndex / HOURS_PER_TAB);
    setActiveTab(`t${day}`);
    // Give Radix a tick to un-hide the pane, then locate the wrapper
    // and scroll. The 260ms gives us headroom on slower devices.
    setTimeout(() => {
      const el = document.querySelector(`[data-hour-index="${hourIndex}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Flash a highlight so the user immediately sees which one opened.
      el.classList.add('ring-4', 'ring-[#FF9F0A]', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-4', 'ring-[#FF9F0A]', 'ring-offset-2');
      }, 2200);
    }, 260);
  }, []);

  // PDF export — Radio Apps branded, only non-empty clocks, 2 large
  // clocks per landscape page. Uses <ExportView/> which lives in a
  // fixed off-screen container so we don't interfere with the visible
  // UI. We mount it on demand via `exporting` and unmount when done.
  const handleExportPdf = async () => {
    const filled = filledClocks(state);
    if (filled.length === 0) {
      toast.error('Δεν έχει προστεθεί καμία κατηγορία σε κανένα ρολόι ακόμα.');
      return;
    }
    setExporting(true);
    // Let ExportView mount and paint before we screenshot.
    await new Promise((r) => setTimeout(r, 400));
    try {
      const rootEl = exportViewRef.current;
      if (!rootEl) throw new Error('Export view not ready');
      await exportBuilderToPdf(rootEl, {
        userEmail: user?.email || user?.username || 'user',
        templateName: templateName || 'Radio Apps Clock Builder',
      });
      toast.success('Το PDF ετοιμάστηκε ✅');
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error(`Αποτυχία εξαγωγής PDF: ${e?.message || 'Άγνωστο σφάλμα'}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[9998]" />
        <Dialog.Content
          data-testid="radio-clock-builder-modal"
          className="fixed inset-0 z-[9999] bg-slate-100 flex flex-col overflow-hidden text-slate-800"
        >
          <div className="flex-shrink-0 bg-gradient-to-r from-[#1B2A4A] via-[#2D3E60] to-[#1B2A4A] text-white px-4 py-3 flex flex-wrap items-center gap-3 shadow">
            <div className="flex-1 min-w-[260px]">
              <Dialog.Title className="text-lg font-bold tracking-wide">RADIO APPS CLOCK BUILDER</Dialog.Title>
              <p className="text-xs text-white/70">
                Σχεδίασε το εβδομαδιαίο σου πρόγραμμα — μια ημέρα, μια ώρα, ένα ρολόι τη φορά.
              </p>
            </div>
            <input
              data-testid="rcb-template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Όνομα template..."
              title="Δώσε ένα όνομα στο template σου (π.χ. «Πρωινή Ζώνη Δευτέρας») πριν πατήσεις Αποθήκευση."
              className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm w-56 text-white placeholder:text-white/50"
            />
            <button
              type="button"
              onClick={handleNew}
              data-testid="rcb-new-btn"
              title="Νέο: Αρχίζεις καινούριο άδειο template. Οι μη-αποθηκευμένες αλλαγές θα χαθούν."
              className="bg-white/10 hover:bg-white/20 rounded px-3 py-1.5 text-sm flex items-center gap-1 text-white"
            >
              <Plus size={14} /> Νέο
            </button>
            <button
              type="button"
              onClick={() => setShowLoader((v) => !v)}
              data-testid="rcb-open-btn"
              title="Άνοιξε: Εμφανίζει τη λίστα με όλα τα templates που έχεις αποθηκεύσει, για να φορτώσεις όποιο θες."
              className="bg-white/10 hover:bg-white/20 rounded px-3 py-1.5 text-sm flex items-center gap-1 text-white"
            >
              <FolderOpen size={14} /> Άνοιξε
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              data-testid="rcb-save-btn"
              title="Αποθήκευση: Σώζει το τρέχον πρόγραμμα στον λογαριασμό σου με το όνομα που έχεις γράψει αριστερά."
              className="bg-emerald-600 hover:bg-emerald-700 rounded px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-60 text-white"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Αποθήκευση
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              data-testid="rcb-export-btn"
              title="PDF: Κατεβάζει ένα εκτυπώσιμο PDF με ένα ρολόι ανά σελίδα (μόνο τα συμπληρωμένα ρολόγια)."
              className="bg-[#FF9F0A] hover:bg-[#FFB84D] text-[#2D0C4B] rounded px-3 py-1.5 text-sm font-bold flex items-center gap-1 disabled:opacity-60"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="rcb-close-btn"
              title="Κλείσιμο του Radio Apps Clock Builder."
              className="bg-white/10 hover:bg-red-600 rounded p-1.5 text-white"
              aria-label="Κλείσιμο"
            >
              <X size={18} />
            </button>
          </div>

          {/* Explanatory helper strip — shows what each header button does */}
          <div className="flex-shrink-0 bg-slate-50 border-b border-slate-200 px-4 py-2 text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
            <span><b>Όνομα template:</b> γράψε τίτλο (π.χ. «Πρωινή Ζώνη»)</span>
            <span className="text-slate-300">•</span>
            <span><b>Νέο:</b> ξεκινάς άδειο</span>
            <span className="text-slate-300">•</span>
            <span><b>Άνοιξε:</b> βλέπεις τα αποθηκευμένα σου</span>
            <span className="text-slate-300">•</span>
            <span><b>Αποθήκευση:</b> σώζει με το όνομα που έγραψες</span>
            <span className="text-slate-300">•</span>
            <span><b>PDF:</b> κατεβάζει εκτυπώσιμο αντίγραφο</span>
          </div>

          {showLoader && (
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 max-h-56 overflow-auto text-slate-800">
              <h4 className="text-xs font-bold uppercase text-[#1B2A4A] mb-2">Τα templates σου</h4>
              {savedTemplates.length === 0 ? (
                <p className="text-xs text-gray-500">Δεν έχεις αποθηκευμένα templates ακόμα.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left text-gray-600">
                    <tr>
                      <th className="py-1">Όνομα</th>
                      <th className="py-1">Τελευταία αλλαγή</th>
                      <th className="py-1 w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedTemplates.map((t) => (
                      <tr key={t.id} className="border-t border-gray-100">
                        <td className="py-1 text-slate-800">{t.name}</td>
                        <td className="py-1 text-gray-500 font-mono text-[11px]">{t.updated_at?.slice(0, 19).replace('T', ' ')}</td>
                        <td className="py-1 flex gap-1 justify-end">
                          <button
                            data-testid={`rcb-load-${t.id}`}
                            onClick={() => handleLoad(t.id)}
                            className="bg-[#1B2A4A] text-white rounded px-2 py-0.5 text-[11px] hover:opacity-90"
                          >
                            Φόρτωσε
                          </button>
                          <button
                            data-testid={`rcb-del-${t.id}`}
                            onClick={() => handleDelete(t.id, t.name)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="flex-1 overflow-auto text-slate-800">
            <div className="p-4 space-y-4 max-w-[1800px] mx-auto">
              <details className="bg-white rounded-lg border border-gray-200 open:shadow-sm">
                <summary className="cursor-pointer px-4 py-2 font-bold text-[#1B2A4A] text-sm uppercase tracking-wide select-none">
                  Κατηγορίες & Τύποι Ομιλίας (edit)
                </summary>
                <div className="p-3 border-t border-gray-100">
                  <CategoriesEditor
                    categories={state.categories}
                    speechTypes={state.speech_types}
                    onCategoriesChange={(cats) => setState((p) => ({ ...p, categories: cats }))}
                    onSpeechTypesChange={(sps) => setState((p) => ({ ...p, speech_types: sps }))}
                  />
                </div>
              </details>

              <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List className="flex flex-wrap gap-1 bg-white p-2 rounded-lg border border-gray-200 sticky top-0 z-10">
                  {TAB_LABELS.map((label, i) => (
                    <Tabs.Trigger
                      key={i}
                      value={`t${i}`}
                      data-testid={`rcb-tab-${i}`}
                      className="text-xs px-3 py-1.5 rounded data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white data-[state=inactive]:bg-slate-100 data-[state=inactive]:text-[#1B2A4A] hover:bg-slate-200 data-[state=active]:hover:bg-[#1B2A4A] transition-colors"
                    >
                      {label}
                    </Tabs.Trigger>
                  ))}
                  <Tabs.Trigger
                    value="overview"
                    data-testid="rcb-tab-overview"
                    className="text-xs px-3 py-1.5 rounded data-[state=active]:bg-[#FF9F0A] data-[state=active]:text-[#2D0C4B] data-[state=inactive]:bg-slate-100 data-[state=inactive]:text-[#1B2A4A] hover:bg-slate-200 font-bold transition-colors"
                  >
                    📊 Overview
                  </Tabs.Trigger>
                </Tabs.List>

                {TAB_LABELS.map((label, tabIdx) => {
                  // During PDF export EVERY pane is un-hidden so html2canvas
                  // can rasterize it. Otherwise Radix's `hidden` prop puts
                  // inactive panes into `display:none` (which cannot be
                  // captured).
                  const isActive = activeTab === `t${tabIdx}`;
                  return (
                    <Tabs.Content
                      key={tabIdx}
                      value={`t${tabIdx}`}
                      forceMount
                      hidden={!exporting && !isActive}
                      className="mt-3"
                    >
                      <div
                        ref={(el) => { tabRefs.current[`t${tabIdx}`] = el; }}
                        className="bg-slate-50 rounded-lg p-2"
                      >
                        <h3 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wide mb-2 px-2">
                          {label} — έως {HOURS_PER_TAB} ωριαία clocks
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          {Array.from({ length: HOURS_PER_TAB }).map((_, offset) => {
                            const hourIndex = tabIdx * HOURS_PER_TAB + offset;
                            return (
                              <div key={hourIndex} data-hour-index={hourIndex} className="rounded-lg transition-shadow">
                                <ClockPanel
                                  title={`Ώρα ${offset + 1}`}
                                  rows={state.hours[hourIndex]?.rows || []}
                                  categories={state.categories}
                                  speechTypes={state.speech_types}
                                  onChange={(rows) => updateHour(hourIndex, rows)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </Tabs.Content>
                  );
                })}

                <Tabs.Content value="overview" forceMount hidden={!exporting && activeTab !== 'overview'} className="mt-3">
                  <div ref={(el) => { tabRefs.current.overview = el; }}>
                    <OverviewPanel state={state} onHourClick={scrollToHour} />
                  </div>
                </Tabs.Content>
              </Tabs.Root>
            </div>
          </div>

          {/* Off-screen print layout — mounted ONLY during PDF export */}
          {exporting && (
            <div ref={exportViewRef} aria-hidden="true">
              <ExportView state={state} templateName={templateName} />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
